<template>
  <div class="session-timer" v-if="isAuthenticated && timeLeft > 0" @click="resetTimer" title="Click to refresh session">
    <i class="fa fa-clock-o"></i>
    <span class="timer-text" :class="{ 'warning': showWarning }">{{ formatTime(timeLeft) }}</span>
  </div>
</template>

<script>
export default {
  name: 'SessionTimer',
  data() {
    return {
      timeLeft: 0,
      timer: null,
      syncTimer: null, // 서버 시간 동기화용 타이머
      showWarning: false,
      warningThresholds: [60, 30, 10], // Show warning at 60, 30, and 10 seconds before expiry
      warningShown: new Set(), // 각 시점별로 팝업 표시 여부 추적
      isResetting: false,
      lastSyncTime: null, // 마지막 동기화 시간
      syncInterval: 30000, // 30초마다 서버와 동기화
      isTabVisible: true, // 탭이 보이는지 여부
      isSessionExpired: false, // 세션 만료 처리 중 플래그 (중복 실행 방지)
      sessionStartTime: null, // 세션 시작/갱신 시점 (timestamp)
      sessionDuration: 0, // 전체 세션 시간 (초)
      sessionRenewedHandler: null // session-renewed 이벤트 핸들러 참조
    }
  },
  computed: {
    isAuthenticated() {
      return this.$store.state.user.isAuthenticated
    }
  },
  methods: {
    formatTime(seconds) {
      const minutes = Math.floor(seconds / 60)
      const remainingSeconds = seconds % 60
      return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`
    },
    startTimer(sessionLifetime) {
      if (this.timer) {
        clearInterval(this.timer)
      }

      // 시간 기반 추적을 위한 변수 설정
      this.sessionDuration = sessionLifetime
      this.sessionStartTime = Date.now()
      this.timeLeft = sessionLifetime
      this.showWarning = false
      this.warningShown = new Set() // 각 시점별 팝업 표시 여부 초기화
      this.lastSyncTime = Date.now()

      this.timer = setInterval(() => {
        if (this.isAuthenticated) {
          // 시간 기반 계산으로 남은 시간 산출 (setInterval throttling 방지)
          var elapsed = Math.floor((Date.now() - this.sessionStartTime) / 1000)
          this.timeLeft = Math.max(0, this.sessionDuration - elapsed)

          // Show warning at multiple thresholds (60, 30, 10 seconds)
          for (var i = 0; i < this.warningThresholds.length; i++) {
            var threshold = this.warningThresholds[i]
            if (this.timeLeft === threshold && !this.warningShown.has(threshold)) {
              this.showWarning = true
              this.warningShown.add(threshold)
              this.$toastr.warning(
                '아무런 동작이 없을 시 ' + threshold + '초 후 자동 로그아웃 됩니다.',
                '[보안] 로그아웃 예고'
              )
              break // 한 번에 하나의 팝업만 표시
            }
          }

          if (this.timeLeft <= 0) {
            this.handleSessionExpired()
          }
        }
      }, 1000)
    },
    handleSessionExpired() {
      // 중복 실행 방지
      if (this.isSessionExpired) {
        return
      }
      this.isSessionExpired = true
      
      // 인증된 상태였는지 확인 (이벤트 발생 전에)
      const wasAuthenticated = this.isAuthenticated
      
      // Clear all timers
      this.clearAllTimers()
      
      // 세션 만료 이벤트를 먼저 발생시켜 팝업이 표시되도록 함
      // (인증 상태를 변경하기 전에)
      if (wasAuthenticated) {
        this.$root.$emit('session-expired')
        window.dispatchEvent(new CustomEvent('session-expired'))
      }
      
      // 세션 상태를 정리 (로그아웃 API 호출 전에)
      if (wasAuthenticated) {
        // Vuex 상태를 직접 업데이트하여 인증 상태 해제
        this.$store.commit('user/loggedOut')
      }
      
      // 세션 스토리지 정리
      sessionStorage.clear()
      
      // 로그아웃 API 호출 시도 (실패해도 무시)
      if (wasAuthenticated) {
        this.$store.dispatch('user/logout').catch(() => {
          // 로그아웃 API 실패는 무시 (이미 세션이 만료된 상태)
        })
      }
    },
    clearAllTimers() {
      if (this.timer) {
        clearInterval(this.timer)
        this.timer = null
      }
      if (this.syncTimer) {
        clearInterval(this.syncTimer)
        this.syncTimer = null
      }
    },
    async syncWithServer() {
      if (!this.isAuthenticated || !this.isTabVisible) {
        return
      }

      try {
        // healthcheck는 세션 유효성 확인만 수행
        // 타이머는 클라이언트 시간 기반으로 관리
        await this.$http.get('/api/healthcheck')
      } catch (error) {
        // 401 에러인 경우 세션 만료로 처리
        if (error.response && error.response.status === 401 && this.isAuthenticated) {
          this.handleSessionExpired()
        } else if (error.response && error.response.status !== 401) {
          console.error('Failed to sync session timer:', error)
        }
      }
    },
    startSyncTimer() {
      if (this.syncTimer) {
        clearInterval(this.syncTimer)
      }
      
      // 주기적으로 서버와 동기화
      this.syncTimer = setInterval(() => {
        this.syncWithServer()
      }, this.syncInterval)
    },
    async resetTimer() {
      if (this.isResetting) return

      this.isResetting = true
      try {
        // /api/user/는 세션을 갱신하고 X-Session-Lifetime 헤더를 반환
        var response = await this.$http.get('/api/user/')
        var sessionLifetime = parseInt(response.headers['x-session-lifetime'])
        if (sessionLifetime && sessionLifetime > 0) {
          this.startTimer(sessionLifetime)
          this.$toastr.success('세션이 갱신되었습니다.', '세션 갱신')
        }
      } catch (error) {
        this.$toastr.error('세션 갱신에 실패했습니다.', '오류')
      } finally {
        this.isResetting = false
      }
    },
    async initializeTimer() {
      // 인증되지 않은 상태에서는 타이머를 초기화하지 않음
      if (!this.isAuthenticated) {
        return
      }

      try {
        // /api/user/는 X-Session-Lifetime 헤더를 반환함 (healthcheck는 반환하지 않음)
        var response = await this.$http.get('/api/user/')
        var sessionLifetime = parseInt(response.headers['x-session-lifetime'])
        if (sessionLifetime && sessionLifetime > 0 && this.isAuthenticated) {
          this.startTimer(sessionLifetime)
          this.startSyncTimer()
        }
      } catch (error) {
        // 401 에러인 경우에만 세션 만료로 처리 (인증된 상태에서만)
        if (error.response && error.response.status === 401 && this.isAuthenticated) {
          this.handleSessionExpired()
        } else {
          console.error('Failed to initialize session timer:', error)
        }
      }
    },
    handleVisibilityChange() {
      if (document.visibilityState === 'visible') {
        this.isTabVisible = true

        // 탭 복귀 시 시간 기반으로 남은 시간 즉시 계산
        if (this.sessionStartTime && this.isAuthenticated) {
          var elapsed = Math.floor((Date.now() - this.sessionStartTime) / 1000)
          this.timeLeft = Math.max(0, this.sessionDuration - elapsed)

          // 이미 만료됐으면 즉시 처리
          if (this.timeLeft <= 0) {
            this.handleSessionExpired()
            return
          }

          // 놓친 경고 처리 (60초, 30초, 10초 중 해당되는 것)
          for (var i = 0; i < this.warningThresholds.length; i++) {
            var threshold = this.warningThresholds[i]
            if (this.timeLeft <= threshold && !this.warningShown.has(threshold)) {
              this.warningShown.add(threshold)
              if (this.timeLeft > 0) {
                this.showWarning = true
                this.$toastr.warning(
                  '아무런 동작이 없을 시 ' + this.timeLeft + '초 후 자동 로그아웃 됩니다.',
                  '[보안] 로그아웃 예고'
                )
              }
              break
            }
          }
        }

        // healthcheck로 세션 유효성 확인
        this.syncWithServer()
      } else {
        this.isTabVisible = false
      }
    }
  },
  mounted() {
    // 인증된 상태에서만 타이머 초기화
    if (this.isAuthenticated) {
      this.initializeTimer()
    }

    // Listen for session lifetime updates from headers
    // 핸들러를 변수에 저장하여 나중에 정확히 같은 참조로 제거할 수 있도록 함
    this.sessionRenewedHandler = (event) => {
      if (this.isAuthenticated) {
        this.startTimer(event.detail.sessionLifetime)
      }
    }
    window.addEventListener('session-renewed', this.sessionRenewedHandler)

    // Page Visibility API로 탭 활성화 감지
    document.addEventListener('visibilitychange', this.handleVisibilityChange)
  },
  beforeDestroy() {
    this.clearAllTimers()
    // 저장된 동일한 참조로 이벤트 리스너 제거
    window.removeEventListener('session-renewed', this.sessionRenewedHandler)
    document.removeEventListener('visibilitychange', this.handleVisibilityChange)
  },
  watch: {
    isAuthenticated(newValue) {
      if (newValue) {
        // 로그인 시 세션 만료 플래그 리셋
        this.isSessionExpired = false
        this.initializeTimer()
      } else {
        // Clear timer when user logs out
        this.clearAllTimers()
        this.timeLeft = 0
        this.showWarning = false
        this.warningShown = new Set()
        this.isSessionExpired = false
      }
    }
  }
}
</script>

<style scoped>
.session-timer {
  display: inline-flex;
  align-items: center;
  color: #fff;
  margin-right: 15px;
  font-size: 0.9em;
  cursor: pointer;
  transition: opacity 0.2s ease;
}

.session-timer:hover {
  opacity: 0.8;
}

.session-timer i {
  margin-right: 5px;
}

.timer-text {
  font-family: monospace;
  background: rgba(255, 255, 255, 0.1);
  padding: 2px 8px;
  border-radius: 4px;
  transition: background-color 0.3s ease;
}

.timer-text.warning {
  background: rgba(255, 193, 7, 0.3);
  animation: pulse 1s infinite;
}

@keyframes pulse {
  0% {
    opacity: 1;
  }
  50% {
    opacity: 0.7;
  }
  100% {
    opacity: 1;
  }
}
</style> 